import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl, shallowEqual, useSelector, getLocale } from 'umi';
import { useLocalStorageState, useMemoizedFn } from 'ahooks';
import { isEmpty } from 'lodash-es';
import classNames from 'classnames';
import AgentSelector from '../../../../AgentCollection/components/AgentSelector';
import { useAgentModelsData } from '../../../../AgentCollection/hooks';
import { EAgentMentionCapability } from '../../../../AgentCollection/types';
import { hasAgentMentionCapability } from '../../../../AgentCollection/utils';
import { useAgentModeSelectorAgents } from './hooks/useAgentModeSelectorAgents';
import SkillMention from '../../SkillMention';
import { ISelectedSkillTagData } from '../../SkillMention/SelectedSkillTag';
import type { ISkillMentionHostProps } from '../../SkillMention/hooks/useSkillMentionHost';
import type { IChartMentionHostProps } from '../../ChartPopover/hooks/useChartMentionHost';
import KnowledgeBaseMention from '../../KnowledgeBaseMention';
import type { ISelectedKnowledgeTagData } from '../../KnowledgeBaseMention/components/SelectedKnowledgeTag';
import type { IKnowledgeMentionHostProps } from '../../KnowledgeBaseMention/hooks/useKnowledgeMentionHost';
import ChartPopover from '../../ChartPopover';
import type { ISelectedChartTagData } from '../../ChartPopover/components/SelectedChartTag';
import InputTriggerHoverTip from '../InputTriggerHoverTip';
import {
  useExclusivePopovers,
  useExternalPopoverSync,
} from '../../hooks/useExclusivePopovers';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { LANGUAGE_EN } from '@home/_config/config';
import { useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { useLocalState } from '../../../../ChatPage/hooks/useLocalState';

import type { IChartPopoverRef } from '../../ChartPopover';

/** 互斥下拉的所有 key */
type TPopoverKey = 'agent' | 'thinking' | 'skill' | 'chart' | 'knowledge';

interface IAgentModeSelectorProps extends Partial<ISkillMentionHostProps>, Partial<IChartMentionHostProps>, Partial<IKnowledgeMentionHostProps> {
  isRecording: boolean;
  template?: string;
  taggingNode?: React.ReactElement;
  canChangeMode?: boolean;
  sessionId?: string;
  from?: string;
  messageId?: string;
  /** 是否为海报生成模式 */
  isPosterMode?: boolean;
  selectedSkills?: ISelectedSkillTagData[];
  onSelectedSkillsChange?: (skills: ISelectedSkillTagData[]) => void;
  /** QAInput 容器 ref，用于定位下拉框（与输入框两侧对齐） */
  inputBoxContainerRef?: React.RefObject<HTMLElement | null>;
  /** 是否处于编辑模式 */
  isEdit?: boolean;
  /** @ 模式下的键盘高亮索引（由 useSkillMentionKeyboard 管理） */
  externalHighlightIndex?: number;
  /** 过滤后的 skill 列表变更回调 */
  onFilteredSkillsChange?: (skills: any[]) => void;
  /** / 模式下选中 skill 后清理触发符并插入引用串 */
  onSlashModeSelect?: (insertLabel?: string) => void;
  /** @ 模式下选中知识库后清理触发符并插入引用串 */
  onAtModeSelect?: (insertLabel?: string) => void;
  /** 按钮模式：整份旧选区文案 → 整份新选区文案 */
  onKbLabelReplace?: (
    oldLabel: string,
    newLabel: string,
    nextItems?: ISelectedKnowledgeTagData[],
  ) => void;
  /** 打开知识库面板前保存编辑器选区 */
  onKbBeforePanelOpen?: () => void;
  /** 按钮模式：在保存的光标处插入新增知识库 */
  onKbInsertAtCursor?: (insertLabel: string) => void;
  /** 按钮模式关面板：按勾选结果同步输入框 mention */
  onKbPanelSync?: (nextItems: ISelectedKnowledgeTagData[]) => void;
  /** 知识库面板关闭后清理冻结插入偏移 */
  onKbPanelCommit?: () => void;
  /** 图表浮窗确认回调（回传结构化的选中图表/报表数据，gameCode 标识当前操作的游戏） */
  onChartConfirm?: (selectedCharts: ISelectedChartTagData[], gameCode: string) => void;
  /** 图表浮窗 ref（用于外部通过 tag 点击打开弹层） */
  chartPopoverRef?: React.RefObject<IChartPopoverRef | null>;
  /** 外部已选中的知识库引用列表 */
  selectedKnowledge?: ISelectedKnowledgeTagData[];
  onSelectedKnowledgeChange?: (items: ISelectedKnowledgeTagData[]) => void;
  /** 外部已选中的图表列表（传给 ChartPopover 用于反推勾选态） */
  selectedCharts?: ISelectedChartTagData[];
  /** 当前会话最后一轮对话消息，用于历史会话同步上一轮 Agent 和思考模式 */
  lastMessages?: {
    modeId?: string;
    mode?: string;
    Mode?: string;
    messageId?: string;
    [key: string]: any;
  };
  /** 由 QAInput 统一计算的 template（含 sessionTemplateMode / 编辑态知识库回退） */
  effectiveTemplate?: string;
  /** 由 QAInput 统一计算：是否展示 Skill / 图表按钮 */
  isMentionScene?: boolean;
}

const AgentModeSelector: React.FC<IAgentModeSelectorProps> = ({
  isRecording,
  template,
  taggingNode,
  canChangeMode = true,
  sessionId,
  from,
  messageId,
  isPosterMode,
  selectedSkills = [],
  onSelectedSkillsChange,
  skillDropdownOpen,
  onSkillDropdownOpenChange,
  skillMentionQuery,
  skillMentionOpenReason,
  skillMentionAnchorRef,
  chartDropdownOpen,
  onChartDropdownOpenChange,
  chartMentionOpenReason,
  inputBoxContainerRef,
  isEdit,
  externalHighlightIndex,
  onFilteredSkillsChange,
  onSlashModeSelect,
  onAtModeSelect,
  onKbLabelReplace,
  onKbBeforePanelOpen,
  onKbInsertAtCursor,
  onKbPanelSync,
  onKbPanelCommit,
  onChartConfirm,
  chartPopoverRef,
  selectedCharts,
  selectedKnowledge = [],
  onSelectedKnowledgeChange,
  knowledgeDropdownOpen,
  onKnowledgeDropdownOpenChange,
  knowledgeMentionQuery,
  knowledgeMentionOpenReason,
  knowledgeMentionAnchorRef,
  openKnowledgeDropdownAsButton,
  lastMessages,
  effectiveTemplate: effectiveTemplateProp,
  isMentionScene = false,
}) => {
  const { formatMessage } = useIntl();
  const history: any = useHistory();
  const isEN = getLocale() === LANGUAGE_EN;
  const isCopilot = from === 'copilot';
  /** Copilot 侧栏输入框底部按钮仅 click 打开面板；Home / ChatPage 保持 hover + click */
  const enableHoverOpen = !isCopilot;
  /** # 触发的 chart 浮窗豁免互斥关闭：textarea 仍有焦点，hover Agent/Skill 不应误关 */
  const isChartHashMode = chartDropdownOpen && chartMentionOpenReason === 'hash';
  const isKnowledgeAtMode = knowledgeDropdownOpen && knowledgeMentionOpenReason === 'at';

  const {
    authorizedAgents,
    modelList,
    selectedAgentId,
    handleAgentClick,
    currentData,
  } = useAgentModelsData();
  const userInfo = useSelector(
    (state: any) => state?.home?.userInfo,
    shallowEqual,
  );

  // 存储每个 Agent 对应的思考模式（key: agentId, value: modeId）
  const [agentModeMap, setAgentModeMap] = useLocalStorageState<Record<string, string>>(
    `__dba_agent_mode_map_${userInfo?.openid}__`,
    {
      defaultValue: {}
    }
  );

  /**
   * 统一的 template：优先使用 URL 参数，fallback 到全局会话模式状态
   */
  const [sessionTemplateMode] = useLocalState<string>('__dba_session_template_mode__', '');
  const { template: urlTemplate, tab: urlTabParam } = useSearchParamsAppoint();
  const urlTab = urlTabParam as string | undefined;
  const effectiveTemplate = (isCopilot ? sessionTemplateMode : (effectiveTemplateProp || urlTemplate || sessionTemplateMode)) as string;
  const currentTab = (effectiveTemplate || urlTab) as string;

  /** 互斥下拉状态机：纯状态机，关闭由各 popover 自带的 outsideClick 处理 */
  const popovers = useExclusivePopovers<TPopoverKey>();
  useExternalPopoverSync(popovers, 'skill', {
    open: skillDropdownOpen,
    onChange: onSkillDropdownOpenChange,
  });
  useExternalPopoverSync(popovers, 'chart', {
    open: chartDropdownOpen,
    onChange: onChartDropdownOpenChange,
  }, {
    disableMutexClose: isChartHashMode,
  });
  useExternalPopoverSync(popovers, 'knowledge', {
    open: knowledgeDropdownOpen,
    onChange: onKnowledgeDropdownOpenChange,
  }, {
    disableMutexClose: isKnowledgeAtMode,
  });
  const agentCtl = popovers.get('agent');
  const thinkingCtl = popovers.get('thinking');

  const closeAgentPopovers = useMemoizedFn(() => {
    agentCtl.close();
    thinkingCtl.close();
  });

  const {
    agentSelectorList,
    handleKnowledgeSelectedChange,
    handleSkillsChange,
    handleChartConfirm,
  } = useAgentModeSelectorAgents({
    authorizedAgents,
    selectedAgentId,
    handleAgentClick,
    effectiveTemplate,
    urlTab,
    selectedKnowledge,
    selectedCharts,
    onSelectedKnowledgeChange,
    onSelectedSkillsChange,
    onChartConfirm,
    closeAgentPopovers,
  });

  // 检查当前 tab 是否有且仅有一个 Agent 的 visibleScenes 命中
  // - 有且仅有 1 个 Agent 命中当前 tab → 隐藏思考模式
  // - 没有任何 Agent 命中当前 tab（无过滤场景）→ 保持原有 hideThinkingMode 逻辑
  const isOnlyOneTabAgent = useMemo(() => {
    if (!currentTab) return false;
    const matchedCount = authorizedAgents?.filter(
      (agent: any) => agent?.visibleScenes?.length && agent.visibleScenes.includes(currentTab),
    )?.length ?? 0;
    return matchedCount === 1;
  }, [authorizedAgents, currentTab]);

  // - 思考模式直接以 lastAgentId 为 key 写入 agentModeMap，不依赖 selectedAgentId 同步传播
  useEffect(() => {
    const lastAgentId = lastMessages?.modeId;
    const lastThinkingModeId = lastMessages?.mode || lastMessages?.Mode;
    if (!lastAgentId) return;

    handleAgentClick(lastAgentId);

    if (lastThinkingModeId) {
      setAgentModeMap({
        ...(agentModeMap || {}),
        [lastAgentId]: lastThinkingModeId,
      });
    }
  }, [sessionId, lastMessages?.messageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 当前 Agent 没有对应思考模式时，默认初始化为首项
  useEffect(() => {
    if (
      selectedAgentId &&
      !agentModeMap[selectedAgentId] &&
      modelList?.[0]?.agentId
    ) {
      setAgentModeMap({
        ...agentModeMap,
        [selectedAgentId]: modelList[0].agentId,
      });
    }
  }, [agentModeMap, selectedAgentId, modelList, setAgentModeMap]);

  // 当存在选中的 skill / chart 时，隐藏 Agent 下拉列表和思考模式选择器
  const hasSelectedSkills = selectedSkills?.length > 0;
  const hasSelectedCharts = (selectedCharts?.length ?? 0) > 0;
  const hasSelectedKnowledge = selectedKnowledge?.length > 0;

  // URL template 为 skill 时（或 Copilot 全局状态指示 skill），隐藏 Agent 选择器和思考模式选择器
  const isSkillTemplate = effectiveTemplate === 'skill';
  const isKnowledgeTemplate = effectiveTemplate === 'knowledge';
  /**
   * 判断是否显示 Agent 集选择器
   * - template !== 'dsReport'  ：DS 报告模式下不展示
   * - !taggingNode || isPosterMode：非数据标注模式，或处于海报模式时展示
   * - canChangeMode             ：当前场景允许切换模式
   * - agentSelectorList?.length > 1：按场景 + 知识库过滤后至少有 2 个可选 Agent 才有切换意义
   * - !hasSelectedSkills        ：已选中 Skill 时隐藏，避免与 Skill 模式冲突
   * - !hasSelectedCharts        ：已选中图表时隐藏，避免与图表模式冲突
   * - !isSkillTemplate          ：URL template=skill 时隐藏，Skill 专属页不需要切换 Agent
   */
  const shouldShowAgentSelector = 
    template !== 'dsReport' && 
    (!taggingNode || isPosterMode) && 
    canChangeMode && 
    agentSelectorList?.length > 1 &&
    !hasSelectedSkills &&
    !hasSelectedCharts &&
    !isSkillTemplate;

  /**
   * 是否强制隐藏思考模式
   * - isOnlyOneTabAgent = true ：当前 tab 有且仅有 1 个 Agent 命中 visibleScenes → 直接隐藏
   * - 否则回退到后端配置        ：使用 currentData.hideThinkingMode 字段决定
   */
  const hideThinking = isOnlyOneTabAgent
    ? true
    : currentData?.hideThinkingMode;

  /**
   * 判断是否显示思考模式选择器
   * - canChangeMode             ：当前场景允许切换模式
   * - !taggingNode || isPosterMode：非数据标注模式，或处于海报模式时展示
   * - !isEmpty(modelList)       ：有可用的思考模式列表
   * - !hideThinking             ：未被上方逻辑强制隐藏
   * - !hasSelectedSkills        ：已选中 Skill 时隐藏，避免与 Skill 模式冲突
   * - !hasSelectedCharts        ：已选中图表时隐藏，避免与图表模式冲突
   * - !isSkillTemplate          ：URL template=skill 时隐藏，Skill 专属页不需要思考模式
   */
  const shouldShowThinkingModeSelector = 
    canChangeMode && 
    (!taggingNode || isPosterMode) && 
    !isEmpty(modelList) && 
    !hideThinking  &&
    !hasSelectedSkills &&
    !hasSelectedCharts &&
    !isSkillTemplate;

  const shouldShowSkillMention =
    isMentionScene &&
    (hasAgentMentionCapability(currentData, EAgentMentionCapability.SKILL) || isSkillTemplate);

  const shouldShowChartMention =
    isMentionScene &&
    hasAgentMentionCapability(currentData, EAgentMentionCapability.CHART);

  const shouldShowKnowledgeMention =
    (shouldShowAgentSelector
      || shouldShowSkillMention
      || isKnowledgeTemplate
      || hasSelectedKnowledge
    )
    && hasAgentMentionCapability(currentData, EAgentMentionCapability.KNOWLEDGE);

  const toggleDeep = useMemoizedFn(
    (mode: any) => {
      if (selectedAgentId) {
        setAgentModeMap({
          ...agentModeMap,
          [selectedAgentId]: mode?.agentId,
        });
      }
      buriedBtnPoint({
        pageId: '700501',
        buttonId: '700501006',
        extInfo: JSON.stringify({
          sessionId,
          mode: mode.agentId,
          modeId: selectedAgentId,
          from,
          messageId,
        }),
        typeId: 'aigc',
      });
    },
  );

  const handleAgentSelectType = useMemoizedFn(
    (agent: { agentId: string; }) => {
      handleAgentClick(agent?.agentId);

      if (agent?.agentId && !agentModeMap[agent.agentId]) {
        const targetAgent = authorizedAgents?.find(
          (item: { agentId: string; }) => item.agentId === agent.agentId,
        );
        if (targetAgent?.thinkingMode?.[0]) {
          setAgentModeMap({
            ...agentModeMap,
            [agent.agentId]: targetAgent.thinkingMode[0],
          });
        }
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501006',
          extInfo: JSON.stringify({
            sessionId,
            mode: targetAgent?.thinkingMode?.[0],
            modeId: agent?.agentId,
            from,
            messageId,
          }),
          typeId: 'aigc',
        });
      }
    },
  );

  const handleMoreClick = useMemoizedFn(() => {
    history.push('/agent/collection');
  });

  /** 同步 SkillMention 外部 host hook，互斥由 useExternalPopoverSync 处理 */
  const handleSkillOpenChange = useMemoizedFn((isOpen: boolean) => {
    onSkillDropdownOpenChange?.(isOpen);
  });

  const customModeHeader = (
    <div className="dba-agent-selector__header">
      <span className="dba-agent-selector__title">
        {formatMessage({ id: 'dba_collection_selector_header_title' })}
      </span>
      <span
        className="dba-agent-selector__more"
        onClick={handleMoreClick}
      >
        {formatMessage({ id: 'dba_collection_selector_header_more' })}
      </span>
    </div>
  );

  const customThinkModeHeader = (
    <div className="dba-agent-selector__header">
      <span className="dba-agent-selector__mode-title">
        {formatMessage({ id: 'dba_collection_selector_header_thinking' })}
      </span>
    </div>
  );

  if (isRecording) {
    return null;
  }

  return (
    <>
      {shouldShowChartMention && (
        <ChartPopover
          ref={chartPopoverRef as any}
          onConfirm={handleChartConfirm}
          selectedCharts={selectedCharts}
          open={chartDropdownOpen}
          onOpenChange={onChartDropdownOpenChange}
          from={from}
          isEdit={isEdit}
        />
      )}
      {shouldShowSkillMention && (
        <SkillMention
          isEN={isEN}
          selectedSkills={selectedSkills}
          onSelectedChange={handleSkillsChange}
          open={skillDropdownOpen}
          onOpenChange={handleSkillOpenChange}
          mentionQuery={skillMentionQuery}
          openReason={skillMentionOpenReason}
          mentionAnchorRef={skillMentionAnchorRef}
          inputBoxContainerRef={inputBoxContainerRef}
          isDropDown={from === 'agentHome' || !!isEdit}
          externalHighlightIndex={externalHighlightIndex}
          onFilteredSkillsChange={onFilteredSkillsChange}
          onSlashModeSelect={onSlashModeSelect}
          enableHoverOpen={enableHoverOpen}
          from={from}
        />
      )}
      {shouldShowKnowledgeMention && (
        <KnowledgeBaseMention
          selectedItems={selectedKnowledge}
          onSelectedChange={handleKnowledgeSelectedChange}
          open={knowledgeDropdownOpen}
          onOpenChange={onKnowledgeDropdownOpenChange}
          onOpenAsButton={openKnowledgeDropdownAsButton}
          mentionQuery={knowledgeMentionQuery}
          openReason={knowledgeMentionOpenReason}
          mentionAnchorRef={knowledgeMentionAnchorRef}
          inputBoxContainerRef={inputBoxContainerRef}
          isDropDown={from === 'agentHome' || !!isEdit}
          from={from}
          onAtModeSelect={onAtModeSelect}
          onLabelReplace={onKbLabelReplace}
          onBeforePanelOpen={onKbBeforePanelOpen}
          onInsertAtCursor={onKbInsertAtCursor}
          onPanelSync={onKbPanelSync}
          onPanelCommit={onKbPanelCommit}
          enableHoverOpen={enableHoverOpen}
        />
      )}

      {shouldShowAgentSelector && (
        <AgentSelector
          list={agentSelectorList}
          selectedId={selectedAgentId}
          onSelect={handleAgentSelectType}
          customHeader={customModeHeader}
          overlayClassName="dba-thinking-home-mode-selector"
          isOpen={agentCtl.isOpen}
          onOpenChange={agentCtl.onOpenChange}
          style={{ width: `${isEN ? 570 : 470}px` }}
          placement={from === 'agentHome' ? 'bottom-start' : undefined}
          flipped={!(from === 'agentHome')}
          escapeWithReference={from === 'agentHome'}
          grouped
          enableHoverOpen={enableHoverOpen}
        />
      )}

      {shouldShowThinkingModeSelector && (
        <InputTriggerHoverTip
          enabled={isCopilot}
          content={formatMessage({ id: 'dba_copilot_input_thinking_hover_tip' })}
          suppressVisible={thinkingCtl.isOpen}
        >
          <AgentSelector
            list={modelList}
            selectedId={agentModeMap[selectedAgentId] || modelList?.[0]?.agentId}
            onSelect={toggleDeep}
            customHeader={customThinkModeHeader}
            className={classNames('dba-thinking-mode-selector', { 'dba-thinking-mode-selector--icon-only': isCopilot })}
            isOpen={thinkingCtl.isOpen}
            onOpenChange={thinkingCtl.onOpenChange}
            style={{ width: `${isEN ? 410 : 240}px` }}
            showIcon={isCopilot || undefined}
            enableHoverOpen={enableHoverOpen}
          />
        </InputTriggerHoverTip>
      )}
    </>
  );
};

export default AgentModeSelector;
